"use strict";

/**
 * Select and return an element
 */
function Core(selector, container) {
  return typeof selector === "string" ? (container || document).querySelector(selector) : selector || null;
}

/**
 * Select and return an array of elements
 */
Core.$$ = (selector, container) => {
  return typeof selector === "string" ? (container || document).querySelectorAll(selector) : selector || null;
};

/**
 * DOM ready callback function
 */
Core.ready = fn => {
  document.addEventListener("DOMContentLoaded", fn);
};

/**
 * Get the index of an element within its parent
 */
Core.index = el => {
  let index = 0;
  while (el = el.previousElementSibling) {
    index++;
  }
  return index;
};

/**
 * Triggers an event on an element
 */
Core.trigger = (el, name) => {
  const event = document.createEvent("HTMLEvents");
  event.initEvent(name, true, false);
  el.dispatchEvent(event);
};

/**
 * Animate an element using css transitions
 */
Core.animate = (el, duration, props) => {
  const old = el.style.transition;
  let transition = "";
  let count = 0;
  let counter = 0;
  const easing = props.ease || "linear";
  const delay = props.delay || 0;
  const complete = props.complete || null;
  for (let key in props) {
    if (key == "ease" || key == "delay" || key == "complete") {
      delete props[key];
      continue;
    }
    const str = key.replace(/([a-z])([A-Z])/g, "$1-$2");
    const tmp = str.toLowerCase() + " " + duration + "s " + easing + " " + delay + "s, ";
    transition += tmp;
    count++;
  }
  el.style.transition = transition.substring(0, transition.length - 2);
  for (let key in props) {
    const str = key.replace(/([a-z])([A-Z])/g, "$1-$2");
    el.style[key] = props[key];
  }
  el.addEventListener("transitionend", end);
  const t = setTimeout(end2, duration * 1000 + delay * 1000 + 50);
  function end(e) {
    if (++counter < count || e.target != e.currentTarget) {
      return false;
    }
    clearTimeout(t);
    counter = 0;
    end2();
  }
  function end2() {
    el.removeEventListener("transitionend", end);
    el.style.transition = old;
    if (complete) {
      complete.call();
    }
  }
};

/**
 * Show or hide an element with a slide animation
 */
Core.slideToggle = (el, complete) => {
  let height = el.offsetHeight;
  let padding = parseInt(getComputedStyle(el)["padding-top"], 10) + parseInt(getComputedStyle(el)["padding-bottom"], 10);

  // Slide up
  if (height - padding > 0) {
    el.style.height = height + "px";
    void el.offsetWidth;
    el.style.height = "0";
    el.addEventListener("transitionend", onEndHide);
  }
  // Slide down
  else {
    el.style.height = "auto";
    height = el.offsetHeight;
    el.style.height = "0";
    el.style.visibility = "visible";
    void el.offsetWidth;
    el.style.height = height + "px";
    el.addEventListener("transitionend", onEndShow);
  }
  function onEndHide() {
    el.style.visibility = "hidden";
    el.removeEventListener("transitionend", onEndHide);
    if (complete) {
      complete();
    }
  }
  function onEndShow() {
    if (el.offsetHeight - padding > 0) {
      el.style.height = "auto";
    }
    el.removeEventListener("transitionend", onEndShow);
    if (complete) {
      complete();
    }
  }
};

/**
 * Trap focus inside an element
 */
Core.trapFocus = el => {
  let focusableElements = [].slice.call(el.querySelectorAll("button, [href], input, select, textarea, [tabindex='0']"));
  focusableElements = focusableElements.filter(item => item.getAttribute("tabindex") != "-1");
  const firstFocusableElement = focusableElements[0];
  const lastFocusableElement = focusableElements[focusableElements.length - 1];
  el.addEventListener("keydown", function (e) {
    var key = e.which || e.keyCode;
    if (key != 9) {
      return;
    }
    if (e.shiftKey) {
      if (document.activeElement === firstFocusableElement) {
        lastFocusableElement.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === lastFocusableElement) {
        firstFocusableElement.focus();
        e.preventDefault();
      }
    }
  });
};

/**
 * Use inert to block the rest of the page
 */
Core.setInert = (el, value) => {
  let current = document.body;
  while (current != el) {
    for (const item of current.children) {
      if (item.tagName == "STYLE" || item.tagName == "SCRIPT" || item.tagName == "NOSCRIPT") continue;
      if (item.contains(el)) {
        current = item;
      } else {
        item.inert = value;
      }
    }
  }
};
class Component {
  constructor(el) {
    this.el = el;
  }
  static setup(arr, ref, selector, settings) {
    arr = arr || document.querySelectorAll(selector);
    if (arr.length == 0) {
      return false;
    }
    const items = [];
    arr.forEach(i => {
      items.push(new ref(i, settings));
    });
    return items;
  }
  init(settings) {
    if (settings) {
      for (var i in settings) {
        if (settings.hasOwnProperty(i)) {
          this.settings[i] = settings[i];
        }
      }
    }
  }
  addEventListener(name, fn) {
    this.el.addEventListener(name, e => {
      fn.call(this, e);
    });
  }
  dispatchEvent(name, props) {
    this.el.dispatchEvent(new CustomEvent(name, props));
  }
}
class Accordion extends Component {
  constructor(el, settings) {
    super(el);
    this.build();
  }
  static setup(arr, settings) {
    return super.setup(arr, Accordion, "[data-accordion]", settings);
  }
  build() {
    // Get id of element
    const elementId = this.el.getAttribute("id");
    if (!elementId) {
      console.warn("Accordion id is missing");
    }

    // Setup click event
    this.el.querySelectorAll("[data-accordion-item]").forEach((item, index) => {
      const labelId = elementId + "-label" + (index + 1);
      const contentId = elementId + "-content" + (index + 1);
      const button = item.firstElementChild.firstElementChild;
      const content = item.lastElementChild;
      content.setAttribute("id", contentId);
      content.setAttribute("aria-labelledby", labelId);
      content.setAttribute("aria-hidden", true);
      button.setAttribute("id", labelId);
      button.setAttribute("aria-controls", contentId);

      // Click event
      const button2 = item.querySelector(".title button");
      if (button2) {
        item.querySelector(".title button").addEventListener("click", e => {
          e.preventDefault();
          item.classList.toggle("active");
          content.setAttribute("aria-hidden", !item.classList.contains("active"));
          Core.slideToggle(content, () => {
            if (content.style.visibility == "hidden") {
              item.classList.remove("active");
            } else {
              item.classList.add("active");
            }
          });

          // Set ARIA
          button.setAttribute("aria-expanded", item.closest("[data-accordion-item]").classList.contains("active"));
        });
      }
    });
  }
}
class Filter extends Component {
  constructor(el, settings) {
    super(el);

    // Settings
    this.settings = {
      selectMultiple: true
    };
    this.init(settings);
    this.all = this.el.querySelector("button[data-id='all']");
    this.items = document.querySelector("[data-filter-items='" + el.getAttribute("data-filter") + "']");
    this.build();
  }
  static setup(arr) {
    return super.setup(arr, Filter, "[data-filter]");
  }
  build() {
    this.el.querySelectorAll("button").forEach(i => {
      i.addEventListener("click", e => {
        const id = i.getAttribute("data-id");

        // All button clicked
        if (id == "all") {
          i.classList.add("active");

          // Remove all
          this.el.querySelectorAll("button").forEach(j => {
            if (i != j) {
              j.classList.remove("active");
            }
          });
        }
        // Any other filter clicked
        else {
          i.classList.toggle("active");
          if (this.settings.selectMultiple) {
            // Hide all button
            if (this.all) {
              this.all.classList.remove("active");
            }
          } else {
            // Hide all except current
            this.el.querySelectorAll("button").forEach(j => {
              if (i != j) {
                j.classList.remove("active");
              }
            });
          }
        }

        // Update items
        this.update();
      });
    });
    this.update();
  }
  update() {
    const filters = [];

    // Update active filters
    this.el.querySelectorAll("button:not([data-id='all'])").forEach(i => {
      if (i.classList.contains("active")) {
        filters.push(i.getAttribute("data-id"));
      }
    });

    // Only run if items element exixts
    if (this.items) {
      // Filters applied, hide all items
      if (filters.length > 0) {
        this.items.querySelectorAll("[data-filter-item]").forEach(i => {
          i.classList.add("hidden");
        });
      }
      // No filters applied, show all items
      else {
        this.items.querySelectorAll("[data-filter-item]").forEach(i => {
          i.classList.remove("hidden");
        });
      }

      // Show filtered items
      filters.forEach(filter => {
        this.items.querySelectorAll("[data-filter-item~='" + filter + "']").forEach(item => {
          item.classList.remove("hidden");
        });
      });
    }
    if (filters.length == 0) {
      if (this.all) {
        this.all.classList.add("active");
      }
    }
    this.dispatchEvent("change", {
      detail: {
        filters: filters
      }
    });
  }
  reset() {
    Core.trigger(this.el.querySelector("button[data-id='all']"), "click");
  }
}
class Header {
  constructor(el) {
    this.el = el;
    this.threshold = 0;
    this.build();
  }

  /*
   * Build the header
   */
  build() {
    window.addEventListener("scroll", e => {
      this.update();
    });
    this.update();
  }
  update() {
    const top = document.documentElement.scrollTop;

    // Small (header is visible but smaller when scrolling)
    if (top > this.threshold) {
      this.el.classList.add("small-mobile");
    } else {
      this.el.classList.remove("small-mobile");
    }
  }
}
class Navigation {
  constructor(el) {
    this.el = el;
    this.toggleButton = document.querySelector("#nav-button");
    this.touched = false;
    this.build();
  }

  /**
   * Build the navigation
   */
  build() {
    // Menu button
    this.toggleButton.addEventListener("click", e => {
      if (!this.isMobileNavOpen()) {
        this.open();
      } else {
        this.close();
      }
    });

    // Close
    document.body.addEventListener("click", e => {
      if (!this.el.contains(e.target)) {
        this.close();
      }
    });

    // Esc key
    this.el.addEventListener("keydown", e => {
      if (e.key == "Escape") {
        // Close sub nav
        if (!this.isMobileNavOpen()) {
          this.el.querySelectorAll(".subnav-button").forEach(item => {
            item.classList.remove("active");
          });
          this.el.querySelectorAll("ul").forEach(item => {
            item.classList.remove("active");
          });
        }

        // Close mobile nav
        this.close();
      }
    });

    // Remove transition when animation complete
    this.el.addEventListener("transitionend", e => {
      if (e.target == this.el.querySelector("div") && e.propertyName == "transform") {
        document.documentElement.classList.remove("nav-open-transition");
      }
    });

    // Add events to parent items
    this.el.querySelectorAll(".menu-item-has-children").forEach(i => {
      if (i.classList.contains("insights")) {
        return;
      }

      // Don't add sub nav button for custom menu
      if (!i.closest(".custom") || i == i.closest(".custom")) {
        i.querySelector("a").insertAdjacentHTML("afterend", '<button class="subnav-button" aria-label="Open sub-menu"><svg width="16" height="9" viewBox="0 0 16 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M.5 2.875 7.772 9h.439L15.5 2.875 13.914 0 8.055 4.787 6.491 6.069l.739-1.884L2.137 0 .5 2.875Z" fill="#FBFBF9"/></svg></button>');
        const subnavButton = i.querySelector(".subnav-button");

        // Subnav button
        subnavButton.addEventListener("click", e => {
          e.stopPropagation();
          this.toggleChildren(i, e.currentTarget);
          if (!this.isMobileNavOpen()) {
            if (i.classList.contains("hover")) {
              this.touched = true;
              this.checkSubNavPosition(i);
            } else {
              if (!i.parentNode.classList.contains("sub-menu")) {
                this.touched = false;
              }
            }
          }
        });
        subnavButton.setAttribute("aria-expanded", false);
      }

      // Touch support for hover menu
      i.addEventListener("click", e => {
        if (!i.closest(".custom") || i == i.closest(".custom")) {
          e.stopPropagation();

          // Close sibling menus if open
          this.closeSiblings(i);
          if (!this.isMobileNavOpen()) {
            if (!this.touched || !i.classList.contains("hover")) {
              e.preventDefault();
            }
            if (!i.classList.contains("hover")) {
              i.classList.add("hover");
              // i.querySelector(".subnav-button").setAttribute("aria-expanded", true);
              this.touched = true;
              this.checkSubNavPosition(i);
            }
          }
        }
      });
    });
    window.addEventListener("resize", e => {
      if (this.inert && !isMobileNavOpen()) {
        this.inert = false;
        Core.setInert(this.el, false);
      }
    });

    // Close all when touched outside
    document.addEventListener("touchstart", e => {
      if (!this.el.contains(e.target)) {
        this.clearSubNav();
      }
    });
  }

  /**
   * Check if mobile nav is open
   */
  isMobileNavOpen() {
    return document.documentElement.classList.contains("nav-open") && this.toggleButton.offsetParent;
  }

  /**
   * Hide the floating sub nav
   */
  clearSubNav(el) {
    const target = el || this.el;
    if (!this.isMobileNavOpen()) {
      target.querySelectorAll(".menu-item-has-children").forEach(item => {
        item.classList.remove("hover");
        if (item.querySelector(".subnav-button")) {
          item.querySelector(".subnav-button").setAttribute("aria-expanded", false);
        }
      });
      this.touched = false;
    }
  }

  /**
   * Close open sibling menus when opening a new menu
   */
  closeSiblings(item) {
    if (!this.isMobileNavOpen() && !item.classList.contains("hover")) {
      item.parentNode.querySelectorAll(":scope > li").forEach(item2 => {
        if (item2.classList.contains("hover")) {
          item2.classList.remove("hover");
        }
      });
    }
  }

  /**
   * Check if the sub nav is out of bounds
   */
  checkSubNavPosition(el) {
    const ul = el.querySelector("ul");
    ul.classList.remove("right");
    const pos = ul.getBoundingClientRect().left;
    const margin = 10;

    // Out of bounds on right side of screen
    if (pos + ul.offsetWidth > window.innerWidth - margin) {
      ul.classList.add("right");
    }

    // Out of bounds on left side of screen
    if (pos < margin) {
      ul.classList.add("left");
    }
  }

  /**
   * Open the mobile nav
   */
  open() {
    document.documentElement.classList.add("nav-open");
    document.documentElement.classList.add("nav-open-transition");
    this.el.querySelector("div").scrollTop = 0;
    this.toggleButton.setAttribute("aria-expanded", true);

    // Set inert
    this.inert = true;
    Core.setInert(this.el, true);
  }

  /**
   * Close the mobile nav
   */
  close() {
    this.clearSubNav();
    if (this.isMobileNavOpen()) {
      document.documentElement.classList.add("nav-open-transition");
    }
    document.documentElement.classList.remove("nav-open");
    this.toggleButton.setAttribute("aria-expanded", false);

    // Remove inert
    if (this.inert) {
      this.inert = false;
      Core.setInert(this.el, false);
    }
  }

  /**
   * Toggle the sub nav
   */
  toggleChildren(item, button) {
    // Close sibling menus if open
    this.closeSiblings(item);
    item.classList.toggle("hover");
    button.setAttribute("aria-expanded", item.classList.contains("hover"));
  }
}
class Sitemap {
  constructor(el) {
    this.el = el;
    this.toggleButton = document.querySelector("#sitemap-button");
    this.build();
  }

  /**
   * Build the navigation
   */
  build() {
    // Menu button
    this.toggleButton.addEventListener("click", e => {
      if (document.documentElement.classList.contains("sitemap-open")) {
        this.close();
      } else {
        this.open();
      }
    });

    // Esc key
    this.el.addEventListener("keydown", e => {
      if (e.key == "Escape") {
        this.close();
      }
    });

    // Remove transition when animation complete
    this.el.addEventListener("transitionend", e => {
      if (e.target == this.el.querySelector("div") && e.propertyName == "transform") {
        document.documentElement.classList.remove("sitemap-open-transition");
      }
    });
  }

  /**
   * Open the sitemap
   */
  open() {
    this.el.querySelector(".items").focus();
    document.documentElement.classList.add("sitemap-open");
    document.documentElement.classList.add("sitemap-open-transition");
    this.el.scrollTop = 0;
    this.toggleButton.setAttribute("aria-expanded", true);

    // Set inert
    this.inert = true;
    Core.setInert(this.el, true);
  }

  /**
   * Close the mobile nav
   */
  close() {
    document.documentElement.classList.add("sitemap-open-transition");
    document.documentElement.classList.remove("sitemap-open");
    this.toggleButton.setAttribute("aria-expanded", false);

    // Remove inert
    if (this.inert) {
      this.inert = false;
      Core.setInert(this.el, false);
    }
  }
}
class Details extends Component {
  constructor(el, settings) {
    super(el);
    this.build();
  }
  static setup(arr, settings) {
    return super.setup(arr, Details, "[data-details]", settings);
  }
  build() {
    // Get id of element
    const elementId = this.el.getAttribute("id");
    if (!elementId) {
      console.warn("Details id is missing");
    }

    // Setup click event
    this.el.querySelectorAll("[data-details-item]").forEach((item, index) => {
      const labelId = elementId + "-label" + (index + 1);
      const contentId = elementId + "-content" + (index + 1);
      const button = item.firstElementChild.firstElementChild;
      const content = item.lastElementChild;
      content.setAttribute("id", contentId);
      content.setAttribute("aria-labelledby", labelId);
      content.setAttribute("aria-hidden", true);
      button.setAttribute("id", labelId);
      button.setAttribute("aria-controls", contentId);

      // Click event
      item.querySelector(".title button").addEventListener("click", e => {
        e.preventDefault();
        item.classList.toggle("active");
        content.setAttribute("aria-hidden", !item.classList.contains("active"));

        // Core.slideToggle(content, () => {
        // 	if(content.style.visibility == "hidden") {
        // 		item.classList.remove("active");
        // 	}
        // 	else {
        // 		item.classList.add("active");
        // 	}
        // });

        // Set ARIA
        button.setAttribute("aria-expanded", item.closest("[data-details-item]").classList.contains("active"));
      });
    });
  }
}
class BackToTop {
  constructor(el) {
    this.el = el;
    this.threshold = 500;
    this.build();
  }

  /*
   * Build the header
   */
  build() {
    this.el.addEventListener("click", e => {
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    });
    window.addEventListener("scroll", e => {
      this.update();
    });
    this.update();
  }
  update() {
    const top = document.documentElement.scrollTop;
    if (top > this.threshold) {
      this.el.classList.add("active");
    } else {
      this.el.classList.remove("active");
    }
  }
}
class App {
  constructor() {
    document.addEventListener("DOMContentLoaded", () => {
      this.start();
    });
  }

  /**
   * App entry point
   */
  start() {
    // Header
    new Header(document.querySelector("#header"));

    // Navigation
    new Navigation(document.querySelector("#navigation"));

    // Sitemap
    new Sitemap(document.querySelector("#sitemap"));

    // Back to top
    new BackToTop(document.querySelector("#back-top-top"));
  }
}
const app = new App();
//# sourceMappingURL=script.js.map
